// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit, WithOptional } from '../_helpers/helper-types';

import type { FileId, UploadableFile } from '../Files/types';
import type { TaskTypeId } from '../ShopSetup/types';

declare const unitOfMeasureIdSymbol: unique symbol;
export type UnitOfMeasureId = string & { [unitOfMeasureIdSymbol]: never };

export type UnitOfMeasure = {
  code: string;
  unitOfMeasureId: UnitOfMeasureId;
  name: string;
};

declare const manufacturerIdSymbol: unique symbol;
export type ManufacturerId = string & { [manufacturerIdSymbol]: never };

export type Manufacturer = {
  manufacturerId: ManufacturerId;
  name: string;
  editable: boolean;
};

declare const partAttributeDefinitionVariantIdSymbol: unique symbol;
export type PartAttributeDefinitionVariantId = string & { [partAttributeDefinitionVariantIdSymbol]: never };

export type PartAttributeDefinitionVariant = {
  partAttributeDefinitionVariantId: PartAttributeDefinitionVariantId;
  name: string;
};

declare const partAttributeDefinitionIdSymbol: unique symbol;
export type PartAttributeDefinitionId = string & { [partAttributeDefinitionIdSymbol]: never };

export type PartAttributeDefinition = {
  partAttributeDefinitionId: PartAttributeDefinitionId;
  name: string;
  allowWriteInVariant: boolean;
  variants: PartAttributeDefinitionVariant[];
  editable: boolean;
  global?: boolean;
};

export type UpdateAttributeBody = StrongOmit<
  PartAttributeDefinition,
  'partAttributeDefinitionId' | 'editable' | 'variants'
> & {
  variants: WithOptional<PartAttributeDefinition['variants'][number], 'partAttributeDefinitionVariantId'>[];
};

declare const partCatalogIdSymbol: unique symbol;
export type PartCatalogId = string & { [partCatalogIdSymbol]: never };

export type PartCatalog = {
  partCatalogId: PartCatalogId;
  name: string;
  editable: boolean;
};

declare const partCategoryIdSymbol: unique symbol;
export type PartCategoryId = string & { [partCategoryIdSymbol]: never };

export type PartCategory = {
  partCategoryId: PartCategoryId;
  parentPartCategoryId?: PartCategoryId;
  partCatalogId: PartCatalogId;
  name: string;
  hasSubCategories: boolean;
  /** List of category names from root to this category (e.g., ['Root', 'Sub', 'Leaf']) */
  path: PartCategory['name'][];
  /** Array of `PartCategoryId`s from root to this category */
  idPath: PartCategoryId[];
  editable: boolean;
  partCount: number;
};

declare const assemblyItemIdSymbol: unique symbol;
export type AssemblyItemId = string & { [assemblyItemIdSymbol]: never };

declare const assemblyTaskIdSymbol: unique symbol;
export type AssemblyTaskId = string & { [assemblyTaskIdSymbol]: never };

export const PART_TYPE_PART_ID = '7088e7a9-f2e4-40ca-ad1a-7e905d896a55' as const;
export const PART_TYPE_ASSEMBLY_ID = 'c0efc3f3-5df0-4708-ab78-d5d3465d6ae4' as const;
export type PartTypeId = typeof PART_TYPE_PART_ID | typeof PART_TYPE_ASSEMBLY_ID;

declare const partIdSymbol: unique symbol;
export type PartId = string & { [partIdSymbol]: never };

declare const customAssemblyJsonSymbol: unique symbol;
export type CustomAssemblyJson = string & { [customAssemblyJsonSymbol]: never }; // raw json

export type CreateEditPartBody = {
  partCategoryId: PartCategoryId;
  unitOfMeasureId: UnitOfMeasureId;
  manufacturerId: ManufacturerId;
  manufacturerCatalogNumber?: string;
  name: string;
  image?: {
    fileName: string;
  };
  description: string | null;
  // Raw JSON
  assemblyEditorLayout: CustomAssemblyJson | null;
  globalPartAttributes: {
    partAttributeDefinitionId: PartAttributeDefinitionId;
    partAttributeDefinitionVariantIds: PartAttributeDefinitionVariantId[];
  }[];
  localPartAttributes: {
    name: string;
    allowWriteInVariant: boolean;
    variants: string[];
  }[];
  assemblyPartItems: {
    assemblyItemId: AssemblyItemId;
    partId: PartId;
    unitQuantity: number;
  }[];
  assemblyWriteInItems: {
    assemblyItemId: AssemblyItemId;
    name: string;
    description?: string | null;
    unitQuantity: number;
    unitOfMeasureId: UnitOfMeasureId;
    manufacturerId?: ManufacturerId;
    manufacturerCatalogNumber?: string; // ?
  }[];
  assemblyTasks: {
    assemblyTaskId: AssemblyTaskId;
    taskTypeId: TaskTypeId;
    predecessorAssemblyTaskIds: AssemblyTaskId[];
  }[];
};

export type AssemblyPartItem = {
  assemblyItemId: AssemblyItemId;
  partId: PartId;
  partTypeId: PartTypeId;
  name: string;
  description: string | null;
  unitQuantity: number;
  unitOfMeasureId: UnitOfMeasureId;
  unitOfMeasureName: string;
  unitOfMeasureCode: string;
  manufacturerId: ManufacturerId;
  manufacturerName: string;
  manufacturerCatalogNumber?: string;
  image?: {
    fileId: FileId;
    fileName: string;
  };
};
export type AssemblyWriteInItem = StrongOmit<AssemblyPartItem, 'partId' | 'partTypeId'>;

export type Part = {
  partId: PartId;
  partTypeId: PartTypeId;
  partCategoryId: PartCategoryId;
  name: string;
  description: string | null;
  unitOfMeasureId: UnitOfMeasureId;
  unitOfMeasureName: string;
  unitOfMeasureCode: string;
  manufacturerId: ManufacturerId;
  manufacturerName: string;
  manufacturerCatalogNumber?: string; // ?
  editable: boolean;
  /**
   * The Assembly editor layout.
   *
   * The backend does not parse/inspect/etc this field. It is used ONLY by the frontend.
   * It is sent as raw JSON (base64 encoded) to the backend and then parsed when the BE sends it to us.
   */
  assemblyEditorLayout: CustomAssemblyJson | null;
  globalPartAttributes: PartAttributeDefinition[];
  localPartAttributes: PartAttributeDefinition[];
  assemblyPartItems: AssemblyPartItem[];
  assemblyWriteInItems: AssemblyWriteInItem[];
  assemblyTasks: {
    assemblyTaskId: AssemblyTaskId;
    taskTypeId: TaskTypeId;
    taskTypeName: string;
    predecessors: {
      assemblyTaskPredecessorId: unknown; // unused
      assemblyTaskId: AssemblyTaskId;
    }[];
  }[];
  image?: UploadableFile;
};

export type PartListPart = StrongOmit<
  Part,
  | 'assemblyEditorLayout'
  | 'assemblyPartItems'
  | 'assemblyWriteInItems'
  | 'assemblyTasks'
  | 'globalPartAttributes'
  | 'localPartAttributes'
>;

export type PartDecision = {
  partId: PartId;
  decisions: {
    partAttributeDefinitionId: PartAttributeDefinitionId;
    partAttributeDefinitionVariantId: PartAttributeDefinitionVariantId | null;
    writeInValue: string | null;
  }[];
};
